import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ReactDOMServer from "react-dom/server";
import ContentPreview from "../../components/Common/ContentPreview";
import DynamicEmailSectionRenderer from "../previewEmail/DynamicEmailSectionRenderer";
import { fetchProductById, fetchProductByIdPublic, fetchShopifyProductFromNewApi, fetchHiddenSections } from "../../utils/Product";
import { getBlogById, fetchShopifyBlogFromNewApi } from "../../utils/blog";

// Simple context provider to mock router context for components in preview mode
const RouterContextProvider = ({ children }) => <>{children}</>;

const UnifiedPreview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [htmlContent, setHtmlContent] = useState("");
  const [cssContent, setCssContent] = useState("");
  const [fetchedProductData, setFetchedProductData] = useState({});
  const [fetchedBlogData, setFetchedBlogData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [contentData, setContentData] = useState({});
  const [hiddenSections, setHiddenSections] = useState([]); 

  // Support both data formats for backward compatibility
  const emailData = location.state?.emailData || {};

  // Handle different content types - determine from URL path if no state (for shared/incognito links)
  const getContentTypeFromPath = () => {
    const pathname = location.pathname;
    if (pathname.includes("/preview-blog/") || pathname.includes("/blog-preview/")) {
      return "blog";
    } else if (pathname.includes("/preview-product-description/")) {
      return "product";
    }
    return null;
  };

  // Determine content type: from state, URL path, or default to "product"
  const contentType = location.state?.contentType || getContentTypeFromPath() || "product";

  // Update contentData when location state or fetched data changes
  useEffect(() => {
    const emailDataFromState = location.state?.emailData || {};
    // Prioritize fetched data over location.state.data for products and blogs
    let newContentData;
    if (contentType === "product") {
      newContentData = fetchedProductData && Object.keys(fetchedProductData).length > 0 
        ? fetchedProductData 
        : location.state?.data || emailDataFromState || {};
    } else if (contentType === "blog") {
      newContentData = fetchedBlogData && Object.keys(fetchedBlogData).length > 0 
        ? fetchedBlogData 
        : location.state?.data || emailDataFromState || {};
    } else {
      newContentData = location.state?.data || emailDataFromState || {};
    }
    setContentData(newContentData);
  }, [location.state?.data, location.state?.emailData, fetchedProductData, fetchedBlogData, contentType]);

  // Support original email flow properties
  const previewMode = location.state?.mode || "desktop";

  // Get styling props from location state with defaults
  const styleSettings = {
    primaryColor: location.state?.primaryColor || "#333333",
    secondaryColor: location.state?.secondaryColor || "#a1894c",
    backgroundColor: location.state?.backgroundColor || "#ffffff",
    fontFamily:
      location.state?.fontFamily ||
      "helvetica, 'helvetica neue', arial, verdana, sans-serif",
  };

  // Fetch product data if documentId is provided in URL params and contentType is product
  useEffect(() => {
    const documentId = params.documentId;
    if (contentType === "product" && documentId && !location.state?.data && !location.state?.emailData) {
      setIsLoading(true);
      
      // Check if product source is provided in location state
      const productSource = location.state?.productSource || "local";
      const isShopifyProduct = productSource === "Shopify";
      
      // For local products, always use the same API endpoint as edit page: /api/v1/products/{id}
      // Use public version if no token (for public preview URLs), otherwise use authenticated version
      const token = localStorage.getItem("token");
      const usePublicApi = !token;
      
      // Call the appropriate API based on product source
      // For local products, always call /api/v1/products/{id} (same as edit page)
      const fetchPromise = isShopifyProduct 
        ? fetchShopifyProductFromNewApi(documentId)
        : (usePublicApi ? fetchProductByIdPublic(documentId) : fetchProductById(documentId));
      
      fetchPromise
        .then((response) => {
          let productData;
          // Extract product data from response - same structure as edit page
          if (response && response.data) {
            if (response.data.data) {
              productData = response.data.data;
            } 
            else if (response.data) {
              productData = response.data;
            }
          } else if (response) {
            productData = response;
          }
          
          if (productData) {
            // Preserve all original product fields (title, description, image, images, collections, variants, etc.)
            const originalProductData = { ...productData };
            
            let forayResponseTitle = null;
            if (productData.foray_response && typeof productData.foray_response === 'string') {
              try {
                const parsedForay = JSON.parse(productData.foray_response);
                const metafields = parsedForay.metafields || productData.metafields || [];
                
                forayResponseTitle = parsedForay.title;
                
                productData = {
                  ...originalProductData,
                  ...parsedForay,
                  metafields: metafields,
                  foray_response: parsedForay,
                  title: parsedForay.title || originalProductData.title,
                  description: parsedForay.description || originalProductData.description,
                  image: parsedForay.image || originalProductData.image,
                  images: parsedForay.images || originalProductData.images,
                  collections: parsedForay.collections || originalProductData.collections,
                  variants: parsedForay.variants || originalProductData.variants,
                  id: originalProductData.id,
                  product_sku: originalProductData.product_sku,
                  tags: originalProductData.tags,
                  price: originalProductData.price,
                  compare_at_price: originalProductData.compare_at_price,
                };
              } catch (e) {
                console.error("Failed to parse foray_response:", e);
                productData = originalProductData;
              }
            } else if (productData.foray_response && typeof productData.foray_response === 'object') {
              forayResponseTitle = productData.foray_response.title;
              
              productData = {
                ...originalProductData,
                ...productData.foray_response,
                title: productData.foray_response.title || originalProductData.title,
                description: productData.foray_response.description || originalProductData.description,
                image: productData.foray_response.image || originalProductData.image,
                images: productData.foray_response.images || originalProductData.images,
                collections: productData.foray_response.collections || originalProductData.collections,
                variants: productData.foray_response.variants || originalProductData.variants,
                id: originalProductData.id,
                product_sku: originalProductData.product_sku,
                tags: originalProductData.tags,
                price: originalProductData.price,
                compare_at_price: originalProductData.compare_at_price,
              };
            }
            
            if (isShopifyProduct) {
              productData.source = "Shopify";
            } else {
              productData.source = productData.source || "local";
            }
            
            if (forayResponseTitle) {
              productData.forayResponseTitle = forayResponseTitle;
            }
            
            setFetchedProductData(productData);
          } else {
            console.error("No product data found for ID:", documentId);
            setFetchedProductData({});
          }
        })
        .catch((error) => {
          console.error("Error fetching product data:", error);
          setFetchedProductData({});
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [params.documentId, contentType, location.state]);

  // Fetch blog data if documentId is provided and contentType is blog
  useEffect(() => {
    const documentId = params.documentId;
    
    if (contentType === "blog" && documentId) {
      setIsLoading(true);
      
      // Check if blog source is provided in location state
      const blogSource = location.state?.blogSource || "local";
      const isShopifyBlog = blogSource === "Shopify";
      const blogId = location.state?.blogId; // Blog ID for Shopify blogs
      
      // Call the appropriate API based on blog source
      const fetchPromise = isShopifyBlog && blogId
        ? fetchShopifyBlogFromNewApi(documentId, blogId)
        : getBlogById(documentId);
      
      fetchPromise
        .then((response) => {
          let blogData = null;
          
          // For local blogs: getBlogById returns { blog: {...} }
          // For Shopify blogs: the API returns the blog object directly
          if (response && response.blog) {
            blogData = response.blog; // Local blog wrapped in { blog: {...} }
          } else if (response && response.data) {
            blogData = response.data;
          } else if (response) {
            blogData = response; // Shopify blog is the response itself
          }
          
          if (blogData) {
            setFetchedBlogData(blogData);
          } else {
            console.error("No blog data found for ID:", documentId);
            setFetchedBlogData({});
          }
        })
        .catch((error) => {
          console.error("Error fetching blog data:", error);
          setFetchedBlogData({});
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [params.documentId, contentType, location.state]);

  // Fetch hidden sections for products using decoded user_id from URL
  useEffect(() => {
    const documentId = params.documentId;
    // Determine content type from pathname if not in state (for shared/incognito links)
    const pathname = location.pathname;
    const isProductPath = pathname.includes("/preview-product-description/");
    const currentContentType = location.state?.contentType || 
      (isProductPath ? "product" : 
       (pathname.includes("/preview-blog/") || pathname.includes("/blog-preview/") ? "blog" : "product"));
    
    if (currentContentType === "product" && documentId) {
      const loadHiddenSections = async () => {
        try {
          // Extract and decode user_id from URL query parameters
          const searchParams = new URLSearchParams(location.search);
          const encodedUserId = searchParams.get("user_id");
          let decodedUserId = null;
          
          if (encodedUserId) {
            try {
              // Decode base64 encoded user_id
              decodedUserId = atob(encodedUserId);
              console.log('UnifiedPreview: Decoded user_id from URL:', decodedUserId);
            } catch (decodeError) {
              console.error('Failed to decode user_id:', decodeError);
            }
          }
          
          console.log('UnifiedPreview: Calling fetchHiddenSections with documentId:', documentId, 'userId:', decodedUserId);
          
          // Call API with decoded user_id (no token needed)
          const response = await fetchHiddenSections("Product", documentId, decodedUserId);
          let allHiddenSections = [];
          
          if (response && response.hidden_sections && Array.isArray(response.hidden_sections)) {
            allHiddenSections = response.hidden_sections;
          } else if (response && response.data && Array.isArray(response.data)) {
            allHiddenSections = response.data;
          } else if (Array.isArray(response)) {
            allHiddenSections = response;
          }
          
          console.log('UnifiedPreview: Received hidden sections:', allHiddenSections);
          
          // Filter only sections where is_hidden === true (for all products)
          const hiddenSectionsWithIsHiddenTrue = allHiddenSections.filter(hs => hs.is_hidden === true);
          
          // Map section names to IDs
          const mapSectionNameToId = (sectionName) => {
            if (sectionName === "product_title" || sectionName.startsWith("product_title")) return "name";
            if (sectionName === "product_about") return "about";
            if (sectionName === "product_benefits") return "benefits";
            if (sectionName === "product_description" || sectionName === "description") return "description";
            if (sectionName === "product_ingredients") return "ingredients";
            if (sectionName === "product_how_to_use") return "howToUse";
            if (sectionName === "product_dosage") return "dosage";
            // For metafields, handle both old format (metafield-{namespace}-{key}-{index}) and new format (metafield--{key})
            if (sectionName.startsWith("metafield--")) {
              // New format: metafield--{key} - return as-is
              return sectionName;
            }
            if (sectionName.startsWith("metafield-")) {
              // Old format: metafield-{namespace}-{key}-{index} - extract key and convert to new format
              const parts = sectionName.split("-");
              if (parts.length >= 4) {
                const key = parts.slice(2, -1).join("-");
                return `metafield--${key}`;
              }
              return sectionName;
            }
            return sectionName;
          };
          
          const hiddenIds = hiddenSectionsWithIsHiddenTrue.map(hs => {
            const sectionName = hs.name || hs.section_name;
            const mappedId = mapSectionNameToId(sectionName);
            
            if (sectionName === "description" || mappedId === "description") {
              return ["description", "about"];
            }
            
            return mappedId;
          }).flat().filter(Boolean);
          
          // Remove duplicates
          const uniqueHiddenIds = [...new Set(hiddenIds)];
          
          console.log('UnifiedPreview: Setting hidden section IDs:', uniqueHiddenIds);
          setHiddenSections(uniqueHiddenIds);
        } catch (error) {
          console.error('UnifiedPreview: Failed to load hidden sections:', error);
          // Don't throw error, just set empty array so preview can still render
          setHiddenSections([]);
        }
      };
      
      loadHiddenSections();
    } else {
      setHiddenSections([]);
    }
  }, [params.documentId, location.pathname, location.search, location.state?.contentType]);

  // Handle back navigation
  const handleBack = () => {
    if (contentType === "product") {
      // Get product data from various sources (same priority as skip step logic)
      const productData = fetchedProductData && Object.keys(fetchedProductData).length > 0
        ? fetchedProductData
        : contentData;
      
      // Check if it's a Shopify product (same logic as DocumentModal skip step)
      const isShopifyProduct = productData?.source === "Shopify";
      const productId = productData?.id || params.documentId;
      
      // Navigate with product ID in URL if available, otherwise use base route (same as skip step)
      const navigationPath = isShopifyProduct
        ? "/edit-product-description"
        : `/edit-product-description/${productId}`;
      
      // Extract sku and tags from product data (same as skip step)
      const sku = productData?.product_sku || productData?.sku || "";
      const tags = productData?.tags || "";
      
      navigate(navigationPath, {
        replace: true,
        state: {
          productData,
          sku,
          tags: typeof tags === 'string' ? tags : (Array.isArray(tags) ? tags.join(", ") : ""),
        },
      });
      return;
    }

    if (location.state?.backUrl) {
      navigate(location.state.backUrl, { replace: true });
    } else {
      // Fallback based on content type
      if (contentType === "product") {
        navigate("/skills/product-description", { replace: true });
      } else if (contentType === "blog") {
        navigate("/skills/blog", { replace: true });
      } else if (contentType === "email") {
        navigate("/skills/email", { replace: true });
      } else {
        navigate("/skills", { replace: true });
      }
    }
  };

  // Generate HTML content based on content type
  useEffect(() => {
    if (isLoading) {
      const loadingMessage = contentType === "blog" ? "Loading blog data..." : contentType === "product" ? "Loading product data..." : "Loading data...";
      setHtmlContent(`<div class="loading-preview">${loadingMessage}</div>`);
      return;
    }

    if (Object.keys(contentData).length === 0) {
      setHtmlContent('<div class="empty-preview">No content to preview</div>');
      return;
    }

    // Handle different content types
    switch (contentType) {
      case "email":
        generateEmailHtml();
        break;
      case "product":
        generateProductHtml();
        break;
      case "blog":
        generateBlogHtml();
        break;
      default:
        setHtmlContent('<div class="empty-preview">Unknown content type</div>');
    }
  }, [contentData, contentType, styleSettings, isLoading, hiddenSections]);

  // Generate Email HTML - follows the pattern in the current email preview
  const generateEmailHtml = () => {
    // Create a wrapper div for the email content
    let emailHtml = '<div class="email-container">';

    Object.entries(contentData).forEach(([sectionKey, sectionData]) => {
      // Skip styleSettings and non-component data
      if (
        sectionKey === "styleSettings" ||
        !sectionData ||
        typeof sectionData !== "object"
      ) {
        return;
      }

      // Handle featured products section
      if (
        sectionKey === "featured_products" &&
        Array.isArray(sectionData.features)
      ) {
        sectionData.features.forEach((feature) => {
          if (feature.component_name) {
            const renderedComponent = ReactDOMServer.renderToString(
              <RouterContextProvider>
                <DynamicEmailSectionRenderer
                  section={feature}
                  styleSettings={styleSettings}
                />
              </RouterContextProvider>
            );
            emailHtml += `<div class="preview-section">${renderedComponent}</div>`;
          }
        });
        return;
      }

      // Handle regular sections
      if (sectionData.component_name) {
        const renderedComponent = ReactDOMServer.renderToString(
          <RouterContextProvider>
            <DynamicEmailSectionRenderer
              section={sectionData}
              styleSettings={styleSettings}
            />
          </RouterContextProvider>
        );
        emailHtml += `<div class="preview-section">${renderedComponent}</div>`;
      }
    });

    emailHtml += "</div>";
    setHtmlContent(emailHtml);

    // Set email styles matching the current implementation
    setCssContent(`
      body {
        font-family: ${styleSettings.fontFamily};
        margin: 0;
        padding: 0;
        color: ${styleSettings.primaryColor};
        background-color: ${styleSettings.backgroundColor || '#ffffff'};
      }
      
      .email-container {
        max-width: 100%;
        margin: 0 auto;
        padding: 20px;
      }
      
      .preview-section {
        margin-bottom: 20px;
      }
      
      .empty-preview {
        text-align: center;
        padding: 40px 20px;
        color: ${styleSettings.primaryColor};
        font-size: 16px;
      }
      
      .loading-preview {
        text-align: center;
        padding: 40px 20px;
        color: ${styleSettings.primaryColor};
        font-size: 16px;
        font-style: italic;
      }
      
      img {
        max-width: 100%;
        height: auto;
      }
      
      h1, h2, h3, h4, h5, h6 {
        color: ${styleSettings.secondaryColor};
      }
      
      p {
        color: ${styleSettings.primaryColor};
      }

      .section-header, .edit-controls, .edit-button {
        display: none !important;
      }

      .email-header p {
        color: ${styleSettings.primaryColor} !important;
      }
      
      .email-header p strong {
        color: ${styleSettings.secondaryColor} !important;
      }
      
      .description-text, .productDescription {
        color: ${styleSettings.primaryColor} !important;
      }
      
      a {
        color: ${styleSettings.primaryColor};
        text-decoration: none;
      }
      
      .button {
        display: inline-block;
        background-color: ${styleSettings.primaryColor};
        color: white !important;
        padding: 8px 16px;
        border-radius: 4px;
        text-align: center;
      }
      
      /* Add useful Bootstrap classes that might be missing */
      .d-flex { display: flex !important; }
      .justify-content-center { justify-content: center !important; }
      .justify-content-between { justify-content: space-between !important; }
      .align-items-center { align-items: center !important; }
      .text-center { text-align: center !important; }
      .w-100 { width: 100% !important; }
      .h-100 { height: 100% !important; }
      .img-fluid { max-width: 100%; height: auto; }
      .mb-2 { margin-bottom: 0.5rem !important; }
      .mb-3 { margin-bottom: 1rem !important; }
      .mb-4 { margin-bottom: 1.5rem !important; }
      .px-3 { padding-left: 1rem !important; padding-right: 1rem !important; }
      .py-2 { padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; }
      .py-3 { padding-top: 1rem !important; padding-bottom: 1rem !important; }
    `);
  };

  // Generate Product HTML - Using existing EditProductDescription design
  const generateProductHtml = () => {
    
    // Get data from location state - support both previewData and direct data
    const previewData = location.state?.previewData || contentData;
    const productData = location.state?.productData || contentData;

    // Check if we have a product ID to potentially fetch complete data
    const productId = productData.id || contentData.id || previewData.id;

    let forayData = {};
    let forayResponseFound = false;
    
    if (productData.foray_response) {
      forayResponseFound = true;
      if (typeof productData.foray_response === "string") {
        try {
          forayData = JSON.parse(productData.foray_response);
        } catch (e) {
          console.error("Failed to parse foray_response:", e);
        }
      } else {
        forayData = productData.foray_response;
      }
    }
    // Check in contentData if not found in productData
    else if (contentData.foray_response) {
      forayResponseFound = true;
      if (typeof contentData.foray_response === "string") {
        try {
          forayData = JSON.parse(contentData.foray_response);
        } catch (e) {
          console.error("Failed to parse contentData foray_response:", e);
        }
      } else {
        forayData = contentData.foray_response;
      }
    }
    // Check in previewData if not found elsewhere
    else if (previewData.foray_response) {
      forayResponseFound = true;
      if (typeof previewData.foray_response === "string") {
        try {
          forayData = JSON.parse(previewData.foray_response);
        } catch (e) {
          console.error("Failed to parse previewData foray_response:", e);
        }
      } else {
        forayData = previewData.foray_response;
      }
    }

    // If no foray_response found, check for individual fields in the data sources
    if (!forayResponseFound || Object.keys(forayData).length === 0) {
      // Try to construct forayData from individual fields across all data sources
      const allData = { ...previewData, ...contentData, ...productData };

      // Extract text from rich text metafields (for ingredients, etc.)
      const parseRichText = (value) => {
        if (!value || typeof value !== 'string') return '';
        try {
          const parsed = JSON.parse(value);
          if (parsed.children && Array.isArray(parsed.children)) {
            return parsed.children
              .map(child => child.children?.map(c => c.value).join('') || '')
              .join('');
          }
        } catch (e) {
          return value;
        }
        return value;
      };

      // Extract ingredients from metafields if available
      let ingredientsValue = '';
      if (allData.metafields && Array.isArray(allData.metafields)) {
        const ingredientsMetafield = allData.metafields.find(m => m.key === 'ingredients');
        if (ingredientsMetafield) {
          ingredientsValue = parseRichText(ingredientsMetafield.value);
        }
      }

      let extractedForayTitle = null;
      
      if (allData.foray_response && typeof allData.foray_response === 'string') {
        try {
          const parsed = JSON.parse(allData.foray_response);
          if (parsed && parsed.title) {
            extractedForayTitle = parsed.title;
          }
        } catch (e) {
        }
      } else if (allData.foray_response && typeof allData.foray_response === 'object' && allData.foray_response.title) {
        extractedForayTitle = allData.foray_response.title;
      } else if (allData.forayResponseTitle) {
        extractedForayTitle = allData.forayResponseTitle;
      }
      
      forayData = {
        title: extractedForayTitle || allData.title || "",
        subtitle: allData.subtitle || "",
        teaser: allData.teaser || allData.productabout || allData.body_html?.replace(/<[^>]*>/g, '').substring(0, 200) || "",
        description: allData.description || allData.body_html || "",
        highlights: allData.highlights || [],
        ingredients: allData.ingredients || ingredientsValue || {},
        detailed_ingredients: allData.detailed_ingredients || [],
        how_to_use: allData.how_to_use || {},
        faq: allData.faq || [],
        benefit: allData.benefit || null,
        benefits03: allData.benefits03 || null,
        benefits04: allData.benefits04 || null,
        features: allData.features || [],
        offer_title: allData.offer_title || "",
        offer_description: allData.offer_description || "",
        about: allData.about || allData.about_the_product || allData.body_html?.replace(/<[^>]*>/g, '') || "",
        sectionOrder: allData.sectionOrder || null, 
        metafields: allData.metafields || [],
        collections: allData.collections || [],
        variants: allData.variants || [],
      };

      // Dynamically add all layout sections that start with layout_
      Object.keys(allData).forEach((key) => {
        if (key.startsWith("layout_")) {
          forayData[key] = allData[key];
        }
      });
    }
    const productName =
      forayData.title ||
      productData.title ||
      contentData.title ||
      previewData.productName ||
      "";


    // Handle teaser - skip if it's placeholder text
    let teaser = productData.teaser || contentData.teaser || "";
    if (teaser === "{Generated teaser here}") {
      teaser = "";
    }

    const productAbout =
      teaser ||
      productData.about ||
      contentData.about ||
      previewData.productabout ||
      productData.description ||
      contentData.description ||
      "";


    // Extract hero image - handle both string URLs and object with src property
    let heroImage = "";
    if (previewData.HeroImage) {
      heroImage = previewData.HeroImage;
    } else if (productData.image) {
      heroImage = typeof productData.image === 'string' ? productData.image : productData.image.src;
    } else if (productData.images && productData.images[0]) {
      heroImage = typeof productData.images[0] === 'string' ? productData.images[0] : productData.images[0].src;
    } else if (contentData.image) {
      heroImage = typeof contentData.image === 'string' ? contentData.image : contentData.image.src;
    }


    // Extract product images - handle both arrays of strings and arrays of objects
    let productImages = [];
    if (previewData.ProductImages) {
      productImages = previewData.ProductImages;
    } else if (productData.images) {
      productImages = productData.images.map(img => typeof img === 'string' ? img : img.src);
    } else if (contentData.images) {
      productImages = contentData.images;
    }

    const sections = previewData.section || [];

   const highlights = productData.highlights || contentData.highlights || [];
    const ingredients = productData.ingredients || contentData.ingredients || {};
    const detailedIngredients =
      productData.detailed_ingredients || contentData.detailed_ingredients || [];
    const howToUse = productData.how_to_use || contentData.how_to_use || {};
    const productDescription = productData.description || contentData.description || "";
    const faq = productData.faq || contentData.faq || [];
    const benefit = productData.benefit || contentData.benefit || null;
    const benefits03 = productData.benefits03 || contentData.benefits03 || null;
    const benefits04 = productData.benefits04 || contentData.benefits04 || null;
    const about_the_product =
      productData.about_the_product ||
      productData.about ||
      contentData.about_the_product ||
      contentData.about ||
      "";
    
    // Handle both standard layout keys and custom layout section names
    const getAllLayoutSections = (data) => {
      const layouts = {};
      Object.keys(data).forEach((key) => {
        if (key.startsWith("layout_one_column")) {
          if (!layouts.layout_one_column) layouts.layout_one_column = [];
          layouts.layout_one_column.push({ key, data: data[key] });
        } else if (key.startsWith("layout_two_columns")) {
          if (!layouts.layout_two_columns) layouts.layout_two_columns = [];
          layouts.layout_two_columns.push({ key, data: data[key] });
        } else if (key.startsWith("layout_three_columns")) {
          if (!layouts.layout_three_columns) layouts.layout_three_columns = [];
          layouts.layout_three_columns.push({ key, data: data[key] });
        } else if (key.startsWith("layout_four_columns")) {
          if (!layouts.layout_four_columns) layouts.layout_four_columns = [];
          layouts.layout_four_columns.push({ key, data: data[key] });
        } else if (key.startsWith("layout_five_columns")) {
          if (!layouts.layout_five_columns) layouts.layout_five_columns = [];
          layouts.layout_five_columns.push({ key, data: data[key] });
        } else if (key.startsWith("layout_six_columns")) {
          if (!layouts.layout_six_columns) layouts.layout_six_columns = [];
          layouts.layout_six_columns.push({ key, data: data[key] });
        } else if (key.startsWith("layout_card")) {
          if (!layouts.layout_card) layouts.layout_card = [];
          layouts.layout_card.push({ key, data: data[key] });
        }
      });
      return layouts;
    };

    const allLayoutSections = getAllLayoutSections(forayData);
    const productDataLayoutSections = getAllLayoutSections(productData);

    // Merge layout sections from both sources
    Object.keys(productDataLayoutSections).forEach((layoutType) => {
      if (!allLayoutSections[layoutType]) {
        allLayoutSections[layoutType] = productDataLayoutSections[layoutType];
      } else {
        // Merge arrays if both exist
        allLayoutSections[layoutType] = [
          ...allLayoutSections[layoutType],
          ...productDataLayoutSections[layoutType].filter(
            (item) =>
              !allLayoutSections[layoutType].some(
                (existing) => existing.key === item.key
              )
          ),
        ];
      }
    });

    // For backward compatibility, create the old variables with the first item
    const layout_one_column =
      allLayoutSections.layout_one_column?.[0]?.data || null;
    const layout_two_columns =
      allLayoutSections.layout_two_columns?.[0]?.data || null;
    const layout_three_columns =
      allLayoutSections.layout_three_columns?.[0]?.data || null;
    const layout_four_columns =
      allLayoutSections.layout_four_columns?.[0]?.data || null;
    const layout_five_columns =
      allLayoutSections.layout_five_columns?.[0]?.data || null;
    const layout_six_columns =
      allLayoutSections.layout_six_columns?.[0]?.data || null;
    const layout_card = allLayoutSections.layout_card?.[0]?.data || null;

    // Additional fields from forayData
    const subtitle = forayData.subtitle || "";
    const offerTitle = forayData.offer_title || "";
    const offerDescription = forayData.offer_description || "";
    const featuredTitle = forayData.featured_title || "";
    const features = forayData.features || [];

    // Create image gallery HTML
    const imageGalleryHtml =
      productImages && productImages.length > 0
        ? `
      <div class="thumbnailContainer">
        <div class="thumbnailWrapper">
          ${productImages
            .map(
              (image, index) => `
            <img
              src="${image}"
              alt="Product Image ${index + 1}"
              class="thumbnail"
            />
          `
            )
            .join("")}
        </div>
      </div>
    `
        : "";

    // Create highlights HTML
    const highlightsHtml =
      highlights && highlights.length > 0
        ? `
      <div class="benefitsContainer">
        <div class="row">
          ${highlights
            .map(
              (highlight, index) => `
            <div class="benefitItem">
              <div class="benefitCircle"></div>
              <span class="benefitText">${highlight}</span>
            </div>
          `
            )
            .join("")}
        </div>
      </div>
    `
        : "";

    // Create ingredients HTML
    const ingredientsHtml =
      ingredients &&
      (typeof ingredients === "string"
        ? ingredients.trim()
        : Object.keys(ingredients).length > 0)
        ? `
      <div class="previewSection">
        <h2>Ingredients:</h2>
        ${
          typeof ingredients === "string"
            ? `<p>${ingredients}</p>`
            : `<ul>
            ${Object.entries(ingredients)
              .map(
                ([ingredient, description]) => `
              <li class="listItem">
                <strong>${ingredient}:</strong> ${description}
              </li>
            `
              )
              .join("")}
          </ul>`
        }
      </div>
    `
        : "";

    // Create detailed ingredients HTML
    const detailedIngredientsHtml =
      detailedIngredients && detailedIngredients.length > 0
        ? `
      <div class="previewSection">
        <h2>Detailed Ingredients:</h2>
        <div class="detailed-ingredients-list">
          ${detailedIngredients
            .map((ingredient) => {
              if (typeof ingredient === "string") {
                return `<div class="ingredient-item"><p>${ingredient}</p></div>`;
              } else if (
                typeof ingredient === "object" &&
                ingredient !== null
              ) {
                return `
                <div class="ingredient-item">
                  ${
                    ingredient.ingredient_name
                      ? `<h3 class="ingredient-name">${ingredient.ingredient_name}</h3>`
                      : ""
                  }
                  ${
                    ingredient.description
                      ? `<p class="ingredient-description">${ingredient.description}</p>`
                      : ""
                  }
                  ${
                    ingredient.nutrition_facts
                      ? `<div class="nutrition-facts"><strong>Nutrition Facts:</strong> ${ingredient.nutrition_facts}</div>`
                      : ""
                  }
                </div>
              `;
              }
              return "";
            })
            .join("")}
        </div>
      </div>
    `
        : "";

    // Create how to use HTML
    const howToUseHtml =
      howToUse && Object.keys(howToUse).length > 0
        ? `
      <div class="previewSection">
        <h2>How To Use:</h2>
        <ul>
          ${Object.entries(howToUse)
            .map(
              ([step, details]) => `
            <li class="listItem">
              <strong>${step}:</strong> ${details}
            </li>
          `
            )
            .join("")}
        </ul>
      </div>
    `
        : "";

    const faqHtml =
      faq && faq.length > 0
        ? `
      <div class="previewSection">
        <h2>Frequently Asked Questions:</h2>
        <ul>
          ${faq
            .map(
              (item) => `
            <li class="listItem">
              <strong>Q: ${item.question}</strong>
              <p>A: ${item.answer}</p>
            </li>
          `
            )
            .join("")}
        </ul>
      </div>
    `
        : "";

    const benefitHtml =
      benefit && benefit.headline && benefit.benefits
        ? `
      <div class="previewSection">
        <h2>Product Benefits 01</h2>
        <h3>${benefit.headline}</h3>
        <ul>
          ${benefit.benefits
            .map(
              (benefitItem) => `
            <li class="listItem">
              <strong>${benefitItem.title}:</strong> ${benefitItem.description}
            </li>
          `
            )
            .join("")}
        </ul>
      </div>
    `
        : "";

    const benefits03Html =
      benefits03 && benefits03.headline && benefits03.benefits
        ? `
      <div class="previewSection">
        <h2>Product Benefits 02</h2>
        <h3>${benefits03.headline}</h3>
        <ul>
          ${benefits03.benefits
            .map(
              (benefit) => `
            <li class="listItem">
              <strong>${benefit.title}:</strong> ${benefit.description}
            </li>
          `
            )
            .join("")}
        </ul>
      </div>
    `
        : "";

    const benefits04Html =
      benefits04 && benefits04.headline && benefits04.benefits
        ? `
      <div class="previewSection">
        <h2>Product Benefits 03</h2>
        <h3>${benefits04.headline}</h3>
        <ul>
          ${benefits04.benefits
            .map(
              (benefit) => `
            <li class="listItem">
              <strong>${benefit.title}:</strong> ${benefit.description}
            </li>
          `
            )
            .join("")}
        </ul>
      </div>
    `
        : "";

    const layoutOneColumnHtml =
      layout_one_column && layout_one_column.title && layout_one_column.content
        ? (() => {
            const { title, content } = layout_one_column;
            const lines = content
              .split("\n")
              .filter((line) => line.trim() !== "");

            let html = `<div class="previewSection"><h2>${title}</h2>`;
            let inList = false;

            lines.forEach((line) => {
              if (line.startsWith("- ")) {
                if (!inList) {
                  html += "<ul>";
                  inList = true;
                }
                const itemContent = line
                  .substring(2)
                  .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
                html += `<li class="listItem">${itemContent}</li>`;
              } else {
                if (inList) {
                  html += "</ul>";
                  inList = false;
                }
                html += `<p>${line}</p>`;
              }
            });

            if (inList) {
              html += "</ul>";
            }

            html += "</div>";
            return html;
          })()
        : "";

    const generateMultiColumnHtml = (layoutData, numColumns) => {
      if (
        !layoutData ||
        !layoutData.content ||
        layoutData.content.length === 0
      ) {
        return "";
      }
      const { title, content } = layoutData;
      return `
        <div class="previewSection">
          <h2>${title}</h2>
          <div class="multi-column-container columns-${numColumns}">
            ${content
              .map((col) => {
                const columnContent = Array.isArray(col.content)
                  ? `<ul class="multi-column-list">${col.content
                      .map((item) => `<li class="listItem">${item}</li>`)
                      .join("")}</ul>`
                  : `<p class="multi-column-text">${col.content || ""}</p>`;

                return `
                <div class="multi-column-item">
                  ${
                    col.image
                      ? `<img src="${col.image}" alt="${
                          col.columnTitle || ""
                        }" class="multi-column-image" />`
                      : ""
                  }
                  ${
                    col.columnTitle
                      ? `<h3 class="multi-column-title">${col.columnTitle}</h3>`
                      : ""
                  }
                  ${columnContent}
                </div>
              `;
              })
              .join("")}
          </div>
        </div>
      `;
    };

    const generateCardLayoutHtml = (layoutData) => {
      if (
        !layoutData ||
        !layoutData.content ||
        layoutData.content.length === 0
      ) {
        return "";
      }
      const { title, content } = layoutData;
      return `
        <div class="previewSection">
          <h2>${title}</h2>
          <div class="card-layout-container">
            ${content
              .map(
                (card) => `
              <div class="card-layout-item">
                ${
                  card.image
                    ? `<img src="${card.image}" alt="${
                        card.title || ""
                      }" class="card-layout-image" />`
                    : ""
                }
                <div class="card-layout-content">
                  ${
                    card.title
                      ? `<h3 class="card-layout-title">${card.title}</h3>`
                      : ""
                  }
                  ${
                    card.text
                      ? `<p class="card-layout-text">${card.text}</p>`
                      : ""
                  }
                </div>
              </div>
            `
              )
              .join("")}
          </div>
        </div>
      `;
    };

    const layoutTwoColumnsHtml = generateMultiColumnHtml(layout_two_columns, 2);
    const layoutThreeColumnsHtml = generateMultiColumnHtml(
      layout_three_columns,
      3
    );
    const layoutFourColumnsHtml = generateMultiColumnHtml(
      layout_four_columns,
      4
    );
    const layoutFiveColumnsHtml = generateMultiColumnHtml(
      layout_five_columns,
      5
    );
    const layoutSixColumnsHtml = generateMultiColumnHtml(layout_six_columns, 6);
    const cardLayoutHtml = generateCardLayoutHtml(layout_card);

    // Generate HTML for all custom layout sections beyond the first one
    const generateCustomLayoutSectionsHtml = () => {
      let customHtml = "";

      // Handle additional one column sections
      if (
        allLayoutSections.layout_one_column &&
        allLayoutSections.layout_one_column.length > 1
      ) {
        allLayoutSections.layout_one_column
          .slice(1)
          .forEach((section, index) => {
            if (section.data && section.data.title && section.data.content) {
              const { title, content } = section.data;
              const lines = content
                .split("\n")
                .filter((line) => line.trim() !== "");
              let html = `<div class="previewSection"><h2>${title}</h2>`;
              let inList = false;
              lines.forEach((line) => {
                if (line.startsWith("- ")) {
                  if (!inList) {
                    html += "<ul>";
                    inList = true;
                  }
                  const itemContent = line
                    .substring(2)
                    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
                  html += `<li class="listItem">${itemContent}</li>`;
                } else {
                  if (inList) {
                    html += "</ul>";
                    inList = false;
                  }
                  html += `<p>${line}</p>`;
                }
              });
              if (inList) {
                html += "</ul>";
              }
              html += "</div>";
              customHtml += html;
            }
          });
      }

      // Handle additional multi-column sections
      [
        "layout_two_columns",
        "layout_three_columns",
        "layout_four_columns",
        "layout_five_columns",
        "layout_six_columns",
      ].forEach((layoutType, index) => {
        const columnCount = index + 2;
        if (
          allLayoutSections[layoutType] &&
          allLayoutSections[layoutType].length > 1
        ) {
          allLayoutSections[layoutType].slice(1).forEach((section) => {
            customHtml += generateMultiColumnHtml(section.data, columnCount);
          });
        }
      });

      // Handle additional card layout sections
      if (
        allLayoutSections.layout_card &&
        allLayoutSections.layout_card.length > 1
      ) {
        allLayoutSections.layout_card.slice(1).forEach((section) => {
          customHtml += generateCardLayoutHtml(section.data);
        });
      }

      return customHtml;
    };

    const customLayoutSectionsHtml = generateCustomLayoutSectionsHtml();

    // Create dynamic sections HTML
    const dynamicSectionsHtml =
      sections && sections.length > 0
        ? sections
            .map(
              (section) => `
      <div class="previewSection">
        <h2>${section.title}:</h2>
        ${
          Array.isArray(section.content)
            ? `<ul>${section.content
                .map((item) => `<li class="listItem">${item}</li>`)
                .join("")}</ul>`
            : typeof section.content === "object"
            ? `<ul>${Object.entries(section.content)
                .map(
                  ([key, value]) => `
              <li class="listItem">
                <strong>${key}:</strong> ${value}
              </li>
            `
                )
                .join("")}</ul>`
            : `<p>${section.content || ""}</p>`
        }
      </div>
    `
            )
            .join("")
        : "";

    // Create features HTML if features exist
    const featuresHtml =
      features && features.length > 0
        ? `
      <div class="previewSection">
        <h2>Features:</h2>
        <ul>
          ${features
            .map(
              (feature) => `
            <li class="listItem">${feature}</li>
          `
            )
            .join("")}
        </ul>
      </div>
    `
        : "";

    // Create offer section HTML
    const offerHtml =
      offerTitle || offerDescription
        ? `
      <div class="previewSection">
        ${offerTitle ? `<h2>${offerTitle}</h2>` : ""}
        ${offerDescription ? `<p>${offerDescription}</p>` : ""}
      </div>
    `
        : "";

    // Create subtitle HTML
    const subtitleHtml = subtitle
      ? `
      <div class="previewSection">
        <h3 class="subtitle">${subtitle}</h3>
      </div>
    `
      : "";

    // Create featured title HTML
    const featuredTitleHtml = featuredTitle
      ? `
      <div class="previewSection">
        <h2>${featuredTitle}</h2>
      </div>
    `
      : "";

    // Don't show separate description section if it's the same as productAbout
    const showSeparateDescription =
      productDescription && productDescription !== productAbout;

    // Create description section HTML only if it's different from the about text
    const descriptionHtml = showSeparateDescription
      ? `
      <div class="previewSection">
        <h2>Description:</h2>
        <div class="description-content">${productDescription}</div>
      </div>
    `
      : "";

    // Remove the warning banner since data is loading properly
    const warningHtml = "";

    // Build section map with all sections
    const sectionMap = new Map();
    
    // Add all sections to the map
    if (highlightsHtml) sectionMap.set("benefits", highlightsHtml);
    if (featuredTitleHtml) sectionMap.set("featured_title", featuredTitleHtml);
    if (featuresHtml) sectionMap.set("features", featuresHtml);
    if (offerHtml) sectionMap.set("offer", offerHtml);
    if (descriptionHtml) sectionMap.set("description", descriptionHtml);
    if (ingredientsHtml) sectionMap.set("ingredients", ingredientsHtml);
    if (detailedIngredientsHtml) sectionMap.set("detailed_ingredients", detailedIngredientsHtml);
    if (howToUseHtml) sectionMap.set("howToUse", howToUseHtml);
    if (faqHtml) sectionMap.set("faq", faqHtml);
    if (benefitHtml) sectionMap.set("benefit", benefitHtml);
    if (benefits03Html) sectionMap.set("benefits03", benefits03Html);
    if (benefits04Html) sectionMap.set("benefits04", benefits04Html);
    if (layoutOneColumnHtml) sectionMap.set("layout_one_column", layoutOneColumnHtml);
    if (layoutTwoColumnsHtml) sectionMap.set("layout_two_columns", layoutTwoColumnsHtml);
    if (layoutThreeColumnsHtml) sectionMap.set("layout_three_columns", layoutThreeColumnsHtml);
    if (layoutFourColumnsHtml) sectionMap.set("layout_four_columns", layoutFourColumnsHtml);
    if (layoutFiveColumnsHtml) sectionMap.set("layout_five_columns", layoutFiveColumnsHtml);
    if (layoutSixColumnsHtml) sectionMap.set("layout_six_columns", layoutSixColumnsHtml);
    if (cardLayoutHtml) sectionMap.set("layout_card", cardLayoutHtml);
    if (customLayoutSectionsHtml) sectionMap.set("custom_layouts", customLayoutSectionsHtml);
    if (dynamicSectionsHtml) sectionMap.set("dynamic_sections", dynamicSectionsHtml);
    
    // Collections section removed - not shown on public preview
    // const collections = forayData?.collections || productData.collections || contentData.collections || [];
    // if (collections && Array.isArray(collections) && collections.length > 0) {
    //   const collectionsHtml = `
    //     <div class="previewSection">
    //       <h2>Collections:</h2>
    //       <div class="collections-container" style="padding: 15px;">
    //         ${collections.map((collection, index) => `
    //           <div key="${collection.id || index}" style="margin-bottom: 15px; padding: 12px; background: #f8f9fa; border-radius: 5px; border: 1px solid #dee2e6;">
    //             <div style="font-weight: 600; color: #495057; margin-bottom: 5px;">
    //               ${collection.title || 'Untitled Collection'}
    //             </div>
    //             ${collection.handle ? `<div style="font-size: 14px; color: #6b7280; margin-bottom: 5px;">Handle: ${collection.handle}</div>` : ''}
    //             ${collection.collection_url ? `<div style="font-size: 14px; color: #6b7280;"><a href="${collection.collection_url}" target="_blank" rel="noopener noreferrer" style="color: #007bff;">${collection.collection_url}</a></div>` : ''}
    //             ${collection.collection_type ? `<div style="font-size: 12px; color: #868e96; margin-top: 5px;">Type: ${collection.collection_type}</div>` : ''}
    //           </div>
    //         `).join('')}
    //       </div>
    //     </div>
    //   `;
    //   sectionMap.set("collections", collectionsHtml);
    // }
    
    // Add variants section
   // const variants = forayData?.variants || productData.variants || contentData.variants || [];
   let variants = [];
   if (variants && Array.isArray(variants) && variants.length > 0) {
      const variantsHtml = `
        <div class="previewSection">
          <h2>Variants:</h2>
          <div class="variants-container" style="padding: 15px;">
            ${variants.map((variant, index) => `
              <div key="${variant.id || index}" style="margin-bottom: 15px; padding: 12px; background: #f8f9fa; border-radius: 5px; border: 1px solid #dee2e6;">
                <div style="font-weight: 600; color: #495057; margin-bottom: 5px;">
                  ${variant.title || 'Untitled Variant'}
                </div>
                <div style="font-size: 14px; color: #6b7280; margin-bottom: 5px;">
                  <div>Price: ${variant.price || 'N/A'}</div>
                  ${variant.compare_at_price ? `<div>Compare at Price: ${variant.compare_at_price}</div>` : ''}
                  ${variant.sku ? `<div>SKU: ${variant.sku}</div>` : ''}
                  ${variant.barcode ? `<div>Barcode: ${variant.barcode}</div>` : ''}
                  ${variant.inventory_quantity !== undefined ? `<div>Inventory: ${variant.inventory_quantity}</div>` : ''}
                  ${variant.weight ? `<div>Weight: ${variant.weight} ${variant.weight_unit || 'g'}</div>` : ''}
                </div>
                ${(variant.option1 || variant.option2 || variant.option3) ? `
                  <div style="font-size: 12px; color: #868e96; margin-top: 5px;">
                    Options: ${[variant.option1, variant.option2, variant.option3].filter(Boolean).join(', ')}
                  </div>
                ` : ''}
              </div>
            `).join('')}
          </div>
        </div>
      `;
      sectionMap.set("variants", variantsHtml);
    }

    // Build metafields HTML and add to section map
    const metafields = forayData?.metafields || productData.metafields || contentData.metafields || [];
    const metafieldsMap = new Map();
    
    // Filter out collection and variant metafields
    const filteredMetafields = metafields.filter((metafield) => {
      const isCollectionsVariant = 
        (metafield.type && (
          metafield.type.includes('list.collection') || 
          metafield.type.includes('list.variant') ||
          metafield.type.includes('collection') ||
          metafield.type.includes('variant')
        )) ||
        (metafield.key && (
          metafield.key.toLowerCase().includes('collections') ||
          metafield.key.toLowerCase().includes('variant')
        ));
      return !isCollectionsVariant;
    });
    
    filteredMetafields.forEach((metafield, index) => {
      // Generate metafield ID matching the format used in ProductDetailsContainer
      // Format: metafield--${key}
      const metafieldId = metafield.key 
        ? `metafield--${metafield.key}`
        : `metafield--${index}`;
      
      // Build metafield HTML
      let metafieldHtml = '';
      
      // Handle rich text fields
      if (metafield.type === 'rich_text_field' || metafield.type === 'rich_text') {
        try {
          const parsed = typeof metafield.value === 'string' ? JSON.parse(metafield.value) : metafield.value;
          if (parsed && parsed.type === 'root' && parsed.children) {
            // Convert rich text to HTML (simplified version)
            const convertRichTextToHtml = (nodes) => {
              if (!nodes || !Array.isArray(nodes)) return '';
              return nodes.map(node => {
                if (!node) return '';
                switch (node.type) {
                  case 'heading':
                    const level = node.level || 2;
                    const headingContent = node.children ? convertRichTextToHtml(node.children) : '';
                    return `<h${level}>${headingContent}</h${level}>`;
                  case 'paragraph':
                    const paraContent = node.children ? convertRichTextToHtml(node.children) : '';
                    return `<p>${paraContent}</p>`;
                  case 'text':
                    let text = node.value || '';
                    if (node.bold) text = `<strong>${text}</strong>`;
                    if (node.italic) text = `<em>${text}</em>`;
                    if (node.underline) text = `<u>${text}</u>`;
                    return text;
                  case 'link':
                    const linkContent = node.children ? convertRichTextToHtml(node.children) : '';
                    return `<a href="${node.url || '#'}" target="${node.target || '_blank'}" rel="noopener noreferrer">${linkContent}</a>`;
                  case 'list':
                    const listTag = node.listType === 'ordered' ? 'ol' : 'ul';
                    const listContent = node.children ? convertRichTextToHtml(node.children) : '';
                    return `<${listTag}>${listContent}</${listTag}>`;
                  case 'list-item':
                  case 'listItem':
                    const itemContent = node.children ? convertRichTextToHtml(node.children) : '';
                    return `<li>${itemContent}</li>`;
                  default:
                    return '';
                }
              }).join('');
            };
            const htmlContent = convertRichTextToHtml(parsed.children);
            if (htmlContent) {
              const metafieldTitle = metafield.key ? metafield.key.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Metafield';
              metafieldHtml = `<div class="previewSection metafield-content">
                <h2>${metafieldTitle}</h2>
                <div style="margin-top: 10px; white-space: pre-wrap; word-wrap: break-word;">
                  ${htmlContent}
                </div>
              </div>`;
            }
          }
        } catch (e) {
          // Not valid rich text JSON, continue with normal processing
        }
      }
      
      // Handle string values (including judgeme widget content)
      if (!metafieldHtml && metafield.value) {
        let value = typeof metafield.value === 'string' ? metafield.value : JSON.stringify(metafield.value);
        
        // For judgeme and other HTML content, preserve the HTML
        // Escape only if it's not already HTML
        const isHtml = value.includes('<') || value.includes('&lt;') || value.includes('.jdgm-');
        if (!isHtml) {
          // Escape HTML for plain text
          value = value.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
        }
        
        const metafieldTitle = metafield.key ? metafield.key.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Metafield';
        metafieldHtml = `<div class="previewSection metafield-content">
          <h2>${metafieldTitle}</h2>
          <div style="margin-top: 10px; white-space: pre-wrap; word-wrap: break-word;">
            ${value}
          </div>
        </div>`;
      }
      
      // Handle metaobjects
      if (!metafieldHtml && metafield.metaobjects && Array.isArray(metafield.metaobjects) && metafield.metaobjects.length > 0) {
        let metaobjectsHtml = `<div class="previewSection metafield-content">
          <h2>${metafield.key.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h2>
          <div class="metaobjects-container">`;
        
        const convertRichTextToHtml = (nodes) => {
          if (!nodes || !Array.isArray(nodes)) return '';
          return nodes.map(node => {
            if (!node) return '';
            switch (node.type) {
              case 'heading':
                const level = node.level || 2;
                return `<h${level}>${node.children ? convertRichTextToHtml(node.children) : ''}</h${level}>`;
              case 'paragraph':
                return `<p>${node.children ? convertRichTextToHtml(node.children) : ''}</p>`;
              case 'text':
                let text = node.value || '';
                if (node.bold) text = `<strong>${text}</strong>`;
                if (node.italic) text = `<em>${text}</em>`;
                return text;
              case 'list':
                const listTag = node.listType === 'ordered' ? 'ol' : 'ul';
                return `<${listTag}>${node.children ? convertRichTextToHtml(node.children) : ''}</${listTag}>`;
              case 'listItem':
                return `<li>${node.children ? convertRichTextToHtml(node.children) : ''}</li>`;
              default:
                return '';
            }
          }).join('');
        };
        
        const parseRichTextValue = (value) => {
          if (typeof value === 'string') {
            try {
              const parsed = JSON.parse(value);
              if (parsed && parsed.type === 'root') {
                return convertRichTextToHtml(parsed.children);
              }
            } catch (e) {
              // Not JSON, use as-is
            }
          }
          return value || '';
        };
        
        metafield.metaobjects.forEach((metaobject) => {
          const metaobjectType = metaobject.type || '';
          metaobjectsHtml += `<div class="metaobject-item" style="margin-bottom: 20px; padding: 15px; background: #f8f9fa; border-radius: 5px; border: 1px solid #dee2e6;">`;
          metaobjectsHtml += `<div style="margin-bottom: 10px;"><strong>Type: ${metaobjectType || 'N/A'}</strong></div>`;
          
          Object.keys(metaobject).forEach(key => {
            if (key === 'type' || key === 'handle' || key === 'id') return;
            const value = metaobject[key];
            if (value === null || value === undefined) return;
            
            if (Array.isArray(value)) {
              if (value.length === 0) return;
              metaobjectsHtml += `<div style="margin-top: 10px;"><strong>${key.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>`;
              value.forEach((item) => {
                if (typeof item === 'object' && item !== null) {
                  metaobjectsHtml += `<div style="margin-left: 20px; margin-top: 8px; padding: 8px; background: white; border-radius: 3px;">`;
                  Object.keys(item).forEach(itemKey => {
                    if (item[itemKey] !== null && item[itemKey] !== undefined) {
                      const displayValue = typeof item[itemKey] === 'string' ? item[itemKey].replace(/\n/g, '<br>') : item[itemKey];
                      metaobjectsHtml += `<div style="margin-bottom: 5px;">
                        <strong>${itemKey.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> ${displayValue}
                      </div>`;
                    }
                  });
                  metaobjectsHtml += `</div>`;
                } else {
                  metaobjectsHtml += `<div style="margin-left: 20px; margin-top: 5px;">${item}</div>`;
                }
              });
              metaobjectsHtml += `</div>`;
            } else if (typeof value === 'object' && value !== null) {
              metaobjectsHtml += `<div style="margin-top: 10px;"><strong>${key.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>`;
              Object.keys(value).forEach(subKey => {
                if (value[subKey] !== null && value[subKey] !== undefined) {
                  metaobjectsHtml += `<div style="margin-left: 20px; margin-top: 5px;">
                    <strong>${subKey.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> ${value[subKey]}
                  </div>`;
                }
              });
              metaobjectsHtml += `</div>`;
            } else {
              let displayValue = value;
              if (key === 'answer') {
                displayValue = parseRichTextValue(value);
              } else if (typeof value === 'string') {
                displayValue = value.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
              }
              metaobjectsHtml += `<div style="margin-bottom: 8px;"><strong>${key.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> ${displayValue}</div>`;
            }
          });
          metaobjectsHtml += `</div>`;
        });
        metaobjectsHtml += `</div></div>`;
        metafieldHtml = metaobjectsHtml;
      }
      
      // Handle flattened_data
      if (!metafieldHtml && metafield.flattened_data && metafield.flattened_data.fields) {
        let flattenedHtml = `<div class="previewSection metafield-content">
          <h2>${metafield.key.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} - Flattened Data</h2>`;
        if (metafield.flattened_data.fields && typeof metafield.flattened_data.fields === 'object') {
          Object.entries(metafield.flattened_data.fields).forEach(([key, value]) => {
            flattenedHtml += `<div style="margin-bottom: 8px;">
              <strong>${key.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> ${value || ''}
            </div>`;
          });
        }
        flattenedHtml += `</div>`;
        metafieldHtml = flattenedHtml;
      }
      
      if (metafieldHtml) {
        metafieldsMap.set(metafieldId, metafieldHtml);
      }
    });

    // Helper function to check if a section should be hidden
    const isSectionHidden = (sectionId) => {
      // Direct match
      if (hiddenSections.includes(sectionId)) {
        return true;
      }
     if (sectionId.startsWith("metafield--")) {
        // Extract key from sectionId (format: metafield--{key})
        const key = sectionId.replace("metafield--", "");
        
        // Check if any hidden section matches
        return hiddenSections.some(hiddenId => {
          // Direct match
          if (hiddenId === sectionId) return true;
          // Match if hidden section is "metafield--{key}"
          if (hiddenId === `metafield--${key}`) return true;
          // Match if hidden section is just the key
          if (hiddenId === key) return true;
          return false;
        });
      }
      
      return false;
    };

    // Render sections in order
    let orderedSectionsHtml = "";
    const sectionOrder = forayData?.sectionOrder || [];
    
    if (sectionOrder && Array.isArray(sectionOrder) && sectionOrder.length > 0) {
      // Render sections in saved order
      const processedIds = new Set();
      sectionOrder.forEach(sectionId => {
        // Skip hidden sections
        if (isSectionHidden(sectionId)) {
          return;
        }
        
        // Handle metafield sections
        if (sectionId.startsWith("metafield--") || sectionId.startsWith("collections-variant-")) {
          if (metafieldsMap.has(sectionId)) {
            orderedSectionsHtml += metafieldsMap.get(sectionId);
            processedIds.add(sectionId);
          }
          return;
        }
        
        if (sectionMap.has(sectionId)) {
          orderedSectionsHtml += sectionMap.get(sectionId);
          processedIds.add(sectionId);
        }
      });
      
      // Add any sections not in the order (excluding hidden sections)
      sectionMap.forEach((html, sectionId) => {
        if (!processedIds.has(sectionId) && !isSectionHidden(sectionId)) {
          orderedSectionsHtml += html;
        }
      });
      
      // Add any metafields not in the order (excluding hidden sections)
      metafieldsMap.forEach((html, sectionId) => {
        if (!processedIds.has(sectionId) && !isSectionHidden(sectionId)) {
          orderedSectionsHtml += html;
        }
      });
    } else {
      // No saved order, render in default order (excluding hidden sections)
      sectionMap.forEach((html, sectionId) => {
        if (!isSectionHidden(sectionId)) {
          orderedSectionsHtml += html;
        }
      });
      metafieldsMap.forEach((html, sectionId) => {
        if (!isSectionHidden(sectionId)) {
          orderedSectionsHtml += html;
        }
      });
    }

    let productHtml = `
      <div class="previewContainer">
        ${warningHtml}
        <div class="contentWrapper">
          <!-- Left Column - Image Gallery -->
          <div class="leftColumn">
            ${
              heroImage
                ? `<img src="${heroImage}" alt="${productName}" style="width: 100%; max-width: 600px; margin: auto;" />`
                : ""
            }
            ${imageGalleryHtml}
          </div>
          
          <!-- Right Column - Product Name & About -->
          <div class="rightColumn">
            ${!hiddenSections.includes("name") && productName ? `<h2 class="productName">${productName}</h2>` : ""}
            ${subtitle ? `<h3 class="subtitle">${subtitle}</h3>` : ""}
            ${!hiddenSections.includes("about") && productAbout ? `<p class="aboutText">${productAbout}</p>` : ""}
            ${
              !hiddenSections.includes("about") && about_the_product
                ? `<p class="aboutText">${about_the_product}</p>`
                : ""
            }
          </div>
        </div>
        
        <!-- Sections in Order (including metafields) -->
        ${orderedSectionsHtml}
      </div>
    `;

    setHtmlContent(productHtml);

    setCssContent(`
      /* Reset and base styles */
      body {
          font-family: "Arial", sans-serif;
          margin: 0;
          padding: 0;
          overflow-x: hidden;
      }

      /* Container styling */
      .previewContainer {
          max-width: 875px;
          width: 100%;
          background: white;
          padding: 30px;
          border-radius: 10px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
          margin: 40px auto;
          font-family: "Arial", sans-serif;
          box-sizing: border-box;
      }

      /* Section styling */
      .previewSection {
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 15px;
          transition: transform 0.2s;
      }

      .previewSection:hover {
          transform: scale(1.02);
      }

      .previewSection h2 {
          font-size: 20px;
          color: #333;
          margin-bottom: 10px;
          display: inline-block;
          padding-bottom: 5px;
          border-bottom: 1px solid #D04734;
      }

      .previewSection p {
          font-size: 16px;
          color: #555;
          line-height: 1.5;
      }

      .previewSection ul {
          list-style-type: none;
          padding: 0;
      }

      .listItem {
          font-size: 16px;
          color: #444;
          padding: 8px 0;
      }

      .listItem:last-child {
          border-bottom: none;
      }

      .detailed-ingredients-list {
          display: flex;
          flex-direction: column;
          gap: 20px;
      }

      .ingredient-name {
          font-size: 18px;
          font-weight: bold;
          color: #333;
          margin: 0 0 8px 0;
      }

      .ingredient-description {
          font-size: 16px;
          color: #555;
          line-height: 1.5;
          margin: 0 0 8px 0;
      }

      .nutrition-facts {
          font-size: 14px;
          color: #666;
          font-style: italic;
          margin: 0;
      }

      .contentWrapper {
          display: flex;
          gap: 20px;
          margin-bottom: 35px;
      }

      .leftColumn {
          flex: 1;
          max-width: 50%;
      }

      .rightColumn {
          flex: 1;
          max-width: 50%;
          text-align: start; 
      }

      .productName {
          font-size: 28px;
          font-weight: bold;
          margin-top: 55px;
          margin-bottom: 30px;
      }

      .subtitle {
          font-size: 20px;
          font-weight: normal;
          color: #666;
          margin-bottom: 20px;
          font-style: italic;
      }

      .aboutText {
          font-size: 20px;
          white-space: pre-wrap;
          word-wrap: break-word;
      }
      
      .description-content {
          white-space: pre-wrap;
          word-wrap: break-word;
      }
      
      .productDescription {
          white-space: pre-wrap;
          word-wrap: break-word;
      }
      
      .metafield-content {
          white-space: pre-wrap;
          word-wrap: break-word;
      }
      
      .metafield-content > div {
          white-space: pre-wrap;
          word-wrap: break-word;
      }
      
      .metafield-html-content,
      .metafield-text-content {
          white-space: pre-wrap;
          word-wrap: break-word;
      }
      
      .metaobjects-container {
          white-space: pre-wrap;
          word-wrap: break-word;
      }
      
      .metaobjects-container > div {
          white-space: pre-wrap;
          word-wrap: break-word;
      }

      .thumbnailContainer {
          width: 100%;
          overflow-x: auto;
          white-space: nowrap;
          display: flex;
          justify-content: center;
          padding: 10px 0;
          box-sizing: border-box;
      }

      .thumbnailWrapper {
          display: flex;
          gap: 8px;
          justify-content: center;
          flex-wrap: nowrap;
      }

      .thumbnail {
          width: 60px;
          height: 60px;
          object-fit: cover;
          border-radius: 8px;
          cursor: pointer;
          transition: transform 0.2s ease-in-out;
      }

      .thumbnail:hover {
          transform: scale(1.1);
      }

      .thumbnailContainer::-webkit-scrollbar {
          height: 5px;
      }

      .thumbnailContainer::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 10px;
      }

      .thumbnailContainer::-webkit-scrollbar-track {
          background: transparent;
      }

      .benefitsContainer {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
          margin-top: 50px;
          margin-bottom: 50px;
      }

      .row {
          display: flex;
          flex-wrap: wrap;
          width: 100%;
          gap: 1rem;
          margin-top: 40px;
          justify-content: center;
      }

      .benefitItem {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: #666;
      }

      .benefitCircle {
          width: 30px;
          height: 30px;
          border: 1px solid #D04734;
          border-radius: 50%;
          background-color: #D047340F;
      }

      .benefitText {
          font-weight: 300;
          font-size: 16px;
      }

      /* Hide all editing controls */
      .edit-button, .edit-controls, .section-header {
          display: none !important;
      }

      /* Responsive Design */
      @media (max-width: 768px) {
          .previewContainer {
              max-width: 430px;
              width: 100%;
              background: white;
              padding: 15px;
              border-radius: 10px;
              box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
              margin: 20px auto;
          }

          .contentWrapper {
              flex-direction: column;
              align-items: center; 
              gap: 15px;
          }

          .leftColumn, .rightColumn {
              max-width: 100%;
              text-align: center;
          }

          .productName {
              font-size: 18px;
              margin-top: 10px;
          }

          .subtitle {
              font-size: 16px;
              margin-bottom: 15px;
          }

          .aboutText {
              font-size: 16px;
              color: #555;
              line-height: 1.5;
          }

          .thumbnailContainer {
              overflow-x: auto;
              padding: 5px 0;
          }

          .previewSection h2 {
              font-size: 16px;
              color: #333;
              margin-bottom: 10px;
              margin-top: 0;
              display: block;
              padding-bottom: 5px;
              border-bottom: 1px solid #D04734;
              width: 100%;
          }

          .previewSection p {
              font-size: 14px;
              color: #555;
              line-height: 1.5;
          }

          .listItem {
              font-size: 14px;
              padding: 5px 10px;
              border-radius: 5px;
          }

          .benefitText {
              font-weight: 300;
              font-size: 14px;
          }

          .benefitCircle {
              width: 20px;
              height: 20px;
              border: 1px solid #D04734;
              border-radius: 50%;
              background-color: #D047340F;
          }

          .row {
              display: flex;
              flex-wrap: wrap;
              width: 100%;
              gap: 2rem;
              margin-top: 15px;
          }
      }
    `);
  };

  // Helper function to render content as HTML string
  const renderContentAsHtml = (content) => {
    if (!content) return "";
    
    let contentString;
    if (typeof content === 'string') {
      contentString = content;
    } else if (Array.isArray(content)) {
      contentString = content.join('\n');
    } else {
      contentString = String(content || '');
    }
    
    // Check if content has table format
    const hasTable = contentString.split("\n").filter((line) => line.includes("|")).length >= 2;
    
    if (hasTable) {
      const lines = contentString.split("\n").filter((line) => line.trim());
      const tableLines = lines.filter((line) => line.includes("|"));
      if (tableLines.length === 0) return contentString;
      
      const headerLine = tableLines.find(line => line.includes("|") && !line.match(/^\s*\|[\s\-\|]+\|\s*$/));
      if (!headerLine) return contentString;
      
      const headers = headerLine.split("|").map(h => h.trim()).filter(h => h);
      const dataRows = tableLines.filter(line => 
        line !== headerLine && 
        !line.match(/^\s*\|[\s\-\|]+\|\s*$/) && 
        line.includes("|") && 
        line.split("|").filter(cell => cell.trim()).length >= 2
      );
      
      let tableHtml = `
        <div style="overflow-x: auto; margin: 16px 0; border: 1px solid #e5e7eb; border-radius: 8px; background-color: #f9fafb;">
          <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
            <thead>
              <tr style="background-color: #E36250; color: white;">
                ${headers.map(header => `
                  <th style="padding: 12px; text-align: left; font-weight: 600; font-size: 14px;">
                    ${header.replace(/\*\*/g, "")}
                  </th>
                `).join('')}
              </tr>
            </thead>
            <tbody>
              ${dataRows.map((row, rowIndex) => {
                const cells = row.split("|").map(cell => cell.trim()).filter(cell => cell);
                if (cells.length < 2) return "";
                return `
                  <tr style="background-color: ${rowIndex % 2 === 0 ? '#ffffff' : '#f8fafc'}; border-bottom: 1px solid #e5e7eb;">
                    ${cells.map((cell, cellIndex) => `
                      <td style="padding: 12px; vertical-align: top; ${cellIndex < cells.length - 1 ? 'border-right: 1px solid #e5e7eb;' : ''}">
                        <span style="font-weight: ${cellIndex === 0 ? 600 : 400}; color: ${cellIndex === 0 ? '#1f2937' : '#4b5563'};">
                          ${cell.replace(/\*\*/g, "")}
                        </span>
                      </td>
                    `).join('')}
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </div>
      `;
      
      return tableHtml;
    }
    
    // Check if content has FAQ format
    if (contentString.includes("**") && contentString.match(/\d+\.\s*\*\*.*?\?\*\*/)) {
      const faqItems = contentString.split(/(?=\d+\.\s*\*\*)/);
      let faqHtml = '<div style="margin-bottom: 16px;">';
      faqItems.forEach((item, index) => {
        if (!item.trim()) return;
        const match = item.match(/(\d+)\.\s*\*\*(.*?\?)\*\*\s*(.*)/s);
        if (match) {
          const [, number, question, answer] = match;
          faqHtml += `
            <div style="margin-bottom: 20px; background-color: #f8fafc; padding: 20px; border-radius: 12px; border: 1px solid #e2e8f0;">
              <div style="font-weight: 600; font-size: 17px; color: #E36250; margin-bottom: 12px; display: flex; align-items: flex-start;">
                <span style="background-color: #E36250; color: white; border-radius: 50%; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; font-size: 14px; margin-right: 12px; flex-shrink: 0;">
                  ${number}
                </span>
                ${question}
              </div>
              <div style="font-size: 15px; color: #4b5563; padding-left: 40px; line-height: 1.6;">
                ${answer.trim()}
              </div>
            </div>
          `;
        }
      });
      faqHtml += '</div>';
      return faqHtml;
    }
    
    // Default: render as paragraphs with formatting
    const paragraphs = contentString.split("\n").filter(p => p.trim());
    return paragraphs.map(paragraph => {
      const formatted = paragraph
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/`(.*?)`/g, '<code style="background-color: #f3f4f6; padding: 2px 6px; border-radius: 4px; font-family: monospace;">$1</code>');
      return `<p style="font-size: 16px; color: #374151; line-height: 1.6; margin-bottom: 12px; text-align: left;">${formatted}</p>`;
    }).join('');
  };
  
  // Generate Blog HTML
  const generateBlogHtml = () => {
    // Extract blog data from contentData
    let blogHtml = "";
    
    // Check if it's a local blog (has final_blog_data)
    if (contentData.final_blog_data) {
      const h1Title = contentData.final_blog_data.h1_title || contentData.about || contentData.name || "";
      const sections = contentData.final_blog_data.sections || [];
      
      // Build HTML from sections
      let sectionsHtml = "";
      sections.forEach((section, index) => {
        const sectionTitle = section.section_title || "";
        const content = section.content || "";
        
        // Render content using helper function
        const renderedContent = renderContentAsHtml(content);
        
        // Add image if exists
        const imageHtml = section.image ? `
          <img src="${section.image}" alt="${sectionTitle}" style="width: 100%; border-radius: 12px; margin-bottom: 16px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);" />
        ` : "";
        
        sectionsHtml += `
          <div class="blog-section" style="margin-bottom: 40px; border-bottom: ${index < sections.length - 1 ? '1px solid #f3f4f6' : 'none'}; padding-bottom: ${index < sections.length - 1 ? '32px' : '0'};">
            ${imageHtml}
            ${sectionTitle ? `
              <h2 class="section-title" style="font-weight: 600; font-size: 26px; margin-bottom: 16px; color: #1f2937; border-left: 4px solid #E36250; padding-left: 16px; line-height: 1.3;">
                ${sectionTitle}
              </h2>
            ` : ""}
            <div class="section-content" style="margin-bottom: 16px;">
              ${renderedContent}
            </div>
          </div>
        `;
      });
      
      blogHtml = `
        <div class="blog-preview" style="max-width: 1000px; margin: 0 auto; padding: 20px; text-align: left;">
          <h1 class="blog-title" style="font-size: 32px; font-weight: 700; color: #111827; margin-bottom: 32px; line-height: 1.2; text-align: left;">
            ${h1Title}
          </h1>
          <div class="blog-content">
            ${sectionsHtml}
          </div>
        </div>
      `;
    } else {
      // Shopify blog format
      const title = contentData.title || "";
      const bodyHtml = contentData.body_html || contentData.content || "";
      const author = contentData.author || "";
      const publishedAt = contentData.published_at || "";
      const image = contentData.image || {};
      const imageSrc = image.src || "";
      
      blogHtml = `
        <div class="blog-preview">
          <h1 class="blog-title">${title}</h1>
          ${
            imageSrc
              ? `<img src="${imageSrc}" alt="${title}" class="featured-image" />`
              : ""
          }
          ${author ? `<div class="blog-meta"><strong>Author:</strong> ${author}</div>` : ""}
          ${publishedAt ? `<div class="blog-meta"><strong>Published:</strong> ${new Date(publishedAt).toLocaleDateString()}</div>` : ""}
          <div class="blog-content">${bodyHtml}</div>
        </div>
      `;
    }

    setHtmlContent(blogHtml);

    setCssContent(`
      body {
        font-family: 'Georgia', serif;
        line-height: 1.6;
        color: #333;
        background-color: #fff;
        margin: 0;
        padding: 0;
      }
      
      .blog-preview {
        max-width: 1000px;
        margin: 0 auto;
        padding: 20px;
      }
      
      .blog-title {
        font-size: 32px;
        font-weight: 700;
        color: #111827;
        margin-bottom: 32px;
        line-height: 1.2;
      }
      
      .featured-image {
        width: 100%;
        height: auto;
        border-radius: 8px;
        margin-bottom: 24px;
      }
      
      .blog-meta {
        color: #666;
        font-size: 14px;
        margin-bottom: 16px;
        padding-bottom: 16px;
        border-bottom: 1px solid #eee;
      }
      
      .blog-content {
        font-size: 18px;
        line-height: 1.8;
      }
      
      .blog-section {
        margin-bottom: 40px;
      }
      
      .section-title {
        font-weight: 600;
        font-size: 26px;
        margin-bottom: 16px;
        color: #1f2937;
        border-left: 4px solid #E36250;
        padding-left: 16px;
        line-height: 1.3;
      }
      
      .section-content {
        margin-bottom: 16px;
      }
      
      .section-content p {
        font-size: 16px;
        color: #374151;
        line-height: 1.6;
        margin-bottom: 12px;
      }
      
      /* Hide all editing controls */
      .edit-button, .edit-controls, .section-header {
        display: none !important;
      }
    `);
  };

  return (
    <ContentPreview
      contentData={contentData}
      htmlContent={htmlContent}
      cssContent={cssContent}
      previewMode={previewMode}
      onBack={handleBack}
      contentType={contentType}
      documentId={params.documentId}
    />
  );
};

export default UnifiedPreview;
